import { useState, useEffect, useMemo } from 'react';

export interface DeviceCapabilities {
  isLowPowerMode: boolean;
  connectionType: 'wifi' | '4g' | '3g' | 'slow' | 'unknown';
  batteryLevel: number | null;
  isCharging: boolean | null;
  memoryInfo: {
    used: number | null;
    total: number | null;
    limit: number | null;
  };
  touchSupport: boolean;
  devicePixelRatio: number;
}

export interface PerformanceSettings {
  animationsEnabled: boolean;
  imageQuality: 'high' | 'medium' | 'low';
  realTimeUpdates: boolean;
  preloadMessages: boolean;
  backgroundSync: boolean;
  reducedMotion: boolean;
  autoLoadImages: boolean;
  virtualScrolling: boolean;
  batchUpdates: boolean;
}

/**
 * Hook for detecting device capabilities and optimizing performance
 * Adapts features based on battery, network, and hardware constraints
 */
export const useMobileOptimization = (): {
  capabilities: DeviceCapabilities;
  settings: PerformanceSettings;
  isOptimized: boolean;
} => {
  const [capabilities, setCapabilities] = useState<DeviceCapabilities>({
    isLowPowerMode: false,
    connectionType: 'unknown',
    batteryLevel: null,
    isCharging: null,
    memoryInfo: {
      used: null,
      total: null,
      limit: null,
    },
    touchSupport: false,
    devicePixelRatio: 1,
  });

  const [reducedMotion, setReducedMotion] = useState(false);

  // Detect device capabilities on mount
  useEffect(() => {
    const detectCapabilities = async () => {
      const newCapabilities: DeviceCapabilities = {
        ...capabilities,
        touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
        devicePixelRatio: window.devicePixelRatio || 1,
      };

      // Battery API
      if ('getBattery' in navigator) {
        try {
          const battery = await (navigator as any).getBattery();
          newCapabilities.batteryLevel = battery.level;
          newCapabilities.isCharging = battery.charging;
          newCapabilities.isLowPowerMode = 
            battery.level < 0.2 || (!battery.charging && battery.level < 0.5);
          
          // Listen for battery changes
          const updateBattery = () => {
            setCapabilities(prev => ({
              ...prev,
              batteryLevel: battery.level,
              isCharging: battery.charging,
              isLowPowerMode: battery.level < 0.2 || (!battery.charging && battery.level < 0.5),
            }));
          };
          
          battery.addEventListener('chargingchange', updateBattery);
          battery.addEventListener('levelchange', updateBattery);
        } catch (error) {
          console.debug('Battery API not supported or access denied');
        }
      }

      // Network API
      if ('connection' in navigator) {
        const connection = (navigator as any).connection;
        if (connection) {
          const effectiveType = connection.effectiveType;
          newCapabilities.connectionType = 
            effectiveType === '4g' ? '4g' : 
            effectiveType === '3g' ? '3g' : 
            effectiveType === '2g' ? 'slow' : 'wifi';
          
          // Listen for connection changes
          const updateConnection = () => {
            setCapabilities(prev => ({
              ...prev,
              connectionType: 
                connection.effectiveType === '4g' ? '4g' : 
                connection.effectiveType === '3g' ? '3g' : 
                connection.effectiveType === '2g' ? 'slow' : 'wifi',
            }));
          };
          
          connection.addEventListener('change', updateConnection);
        }
      }

      // Memory API (Chrome only)
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        newCapabilities.memoryInfo = {
          used: memory.usedJSHeapSize,
          total: memory.totalJSHeapSize,
          limit: memory.jsHeapSizeLimit,
        };
      }

      setCapabilities(newCapabilities);
    };

    detectCapabilities();
  }, []);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mediaQuery.matches);
    
    const listener = (e: MediaQueryListEvent) => {
      setReducedMotion(e.matches);
    };
    
    mediaQuery.addEventListener('change', listener);
    return () => mediaQuery.removeEventListener('change', listener);
  }, []);

  // Calculate performance settings based on capabilities
  const settings = useMemo((): PerformanceSettings => {
    const {
      isLowPowerMode,
      connectionType,
      batteryLevel,
      memoryInfo,
      touchSupport,
    } = capabilities;

    // Determine if we should optimize for performance
    const shouldOptimize = 
      isLowPowerMode || 
      connectionType === 'slow' || 
      connectionType === '3g' ||
      (batteryLevel !== null && batteryLevel < 0.3) ||
      (memoryInfo.used !== null && memoryInfo.limit !== null && 
       memoryInfo.used / memoryInfo.limit > 0.8);

    return {
      // Animations: disabled on low power or slow connections
      animationsEnabled: !shouldOptimize && !reducedMotion,
      
      // Image quality: adaptive based on connection and memory
      imageQuality: 
        connectionType === 'wifi' && !shouldOptimize ? 'high' :
        connectionType === '4g' && !isLowPowerMode ? 'medium' : 'low',
      
      // Real-time updates: disabled on slow connections
      realTimeUpdates: connectionType !== 'slow' && !isLowPowerMode,
      
      // Preloading: only on WiFi with good battery
      preloadMessages: 
        connectionType === 'wifi' && 
        !isLowPowerMode && 
        (batteryLevel === null || batteryLevel > 0.5),
      
      // Background sync: disabled on low power
      backgroundSync: !isLowPowerMode,
      
      // Reduced motion: respect system preference
      reducedMotion,
      
      // Auto-load images: based on connection and settings
      autoLoadImages: 
        connectionType === 'wifi' || 
        (connectionType === '4g' && !shouldOptimize),
      
      // Virtual scrolling: enabled on mobile for large lists
      virtualScrolling: touchSupport || shouldOptimize,
      
      // Batch updates: enabled when optimizing
      batchUpdates: shouldOptimize,
    };
  }, [capabilities, reducedMotion]);

  const isOptimized = 
    capabilities.isLowPowerMode || 
    capabilities.connectionType === 'slow' || 
    capabilities.connectionType === '3g' ||
    (capabilities.batteryLevel !== null && capabilities.batteryLevel < 0.3);

  return {
    capabilities,
    settings,
    isOptimized,
  };
};

/**
 * Hook for adaptive image loading based on device capabilities
 * @param imageUrl - Original image URL
 * @param alt - Alt text for accessibility
 * @returns Optimized image props and loading state
 */
export const useAdaptiveImage = (imageUrl: string, alt: string) => {
  const { settings } = useMobileOptimization();
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Generate optimized image URL based on quality setting
  const optimizedUrl = useMemo(() => {
    if (!imageUrl) return imageUrl;
    
    const url = new URL(imageUrl, window.location.origin);
    
    switch (settings.imageQuality) {
      case 'low':
        url.searchParams.set('q', '40');
        url.searchParams.set('w', '400');
        break;
      case 'medium':
        url.searchParams.set('q', '70');
        url.searchParams.set('w', '800');
        break;
      case 'high':
      default:
        url.searchParams.set('q', '90');
        break;
    }
    
    return url.toString();
  }, [imageUrl, settings.imageQuality]);

  const imageProps = {
    src: settings.autoLoadImages ? optimizedUrl : undefined,
    alt,
    loading: 'lazy' as const,
    onLoad: () => setIsLoaded(true),
    onError: () => setHasError(true),
    style: {
      opacity: isLoaded ? 1 : 0,
      transition: settings.animationsEnabled ? 'opacity 0.3s ease' : 'none',
    },
  };

  return {
    imageProps,
    isLoaded,
    hasError,
    shouldAutoLoad: settings.autoLoadImages,
    quality: settings.imageQuality,
  };
};

/**
 * Hook for performance-aware message loading
 * Adapts loading strategy based on device capabilities
 */
export const useMobileMessageLoading = (conversationId: string) => {
  const { settings, capabilities } = useMobileOptimization();
  const [messages, setMessages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const loadMessages = useMemo(() => {
    // Debounce loading on slow connections
    const delay = capabilities.connectionType === 'slow' ? 500 : 0;
    
    return setTimeout(async (page = 1) => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          mobile: 'true',
          quality: settings.imageQuality,
          limit: settings.batchUpdates ? '20' : '50',
        });

        const response = await fetch(
          `/api/conversations/${conversationId}/messages?${params}`
        );
        const data = await response.json();
        
        if (page === 1) {
          setMessages(data.messages);
        } else {
          setMessages(prev => [...data.messages, ...prev]);
        }
        
        setHasMore(data.hasMore);
        
        // Preload next page on good connections
        if (settings.preloadMessages && data.hasMore) {
          setTimeout(() => loadMessages(page + 1), 1000);
        }
      } catch (error) {
        console.error('Failed to load messages:', error);
      } finally {
        setIsLoading(false);
      }
    }, delay);
  }, [conversationId, settings, capabilities.connectionType]);

  return {
    messages,
    isLoading,
    hasMore,
    loadMessages,
    settings,
  };
}; 